import React from "react";
import { Page } from "../Page";
import { api } from "../../api/api";
import { wf } from "../../types";
import { Modal } from "../../core/ui/Modal";
import { Table } from "../../core/ui/Table";
import { useConditional } from "../../core/hooks/useConditional";
import { newID } from "../../core/utils/unique";
import { useApi } from "../../core/hooks/useApi";
import { CheckboxCol, CopyToClipboardCol, CustomCol, IDCol, LastUpdatedCol, SyncTableCol, TagsCol, TitleCol, VersionCol } from "../../layout/TableCells";
import { Designer } from "../../wf-designer/Designer";
import { FormModal } from "../../data-entry/FormModal";
import { TagEditor } from "../../layout/TagEditor";
import { Text } from "../../core/form/Text";
import { DataContext, useDataContext } from "../../core/hooks/useData";
import { WorkflowIcon } from "../dashboard/CurrentForms";
import { useBulkActions } from "../../core/hooks/useBulkActions";
import { DuplicateModal } from "../../core/ui/DuplicateModal";
import { AutoFilterList } from "../../core/ui/AutoFilterList";
import { useAutoFilter } from "../../core/hooks/useAutoFilter";


export function Workflows(props: {

}) {
	const [token, setToken] = React.useState("");

	const createModal = useConditional(() => {
		return (
			<FormModal
				form="workflow-create"
				obj={{
					id: newID(),
					title: "New Workflow"
				}}
				onCancel={createModal.close}
				onSave={(value: wf) => {

					const wf = {
						...value,
						deleted: 0,
						data: {
							states: [
								{
									id: "new",
									title: "New",
									phase: "In Progress",
									canEditForms: true,
									canDeleteForms: true,
									transitions: [],		
								}
							]
						},
					};
	
					api.wf_post(wf).then(() => {
						setToken(newID());
						createModal.close();
					}).catch(() => {
	
					});
	
				}}
			/>
		)
	});

	return (
		<Page
			compact={true}
			title={{
				text: "Workflows",
				icon: "workflow_color",
				buttons: [
					{ title: "Create" }
				]
			}}
			onButton={(btn) => {
				createModal.show({});
			}}
			
		>
			<WorkflowList key={token} />
			{createModal.render()}
		</Page>
	);
}


export function WorkflowList(props: {

}) {

	const [token, setToken] = React.useState("");

	const wfs = useApi({
		request: () => api.wf_get({}),

		prepareResult: (result: wf[]) => {

			result.sort((a, b) => {
				return (a ? a.title : "").localeCompare(b ? b.title : "");
			});

			return result;
		}
	}, [token]);

	const items = wfs ? wfs : [];
	const { filteredData, onFilterChange } = useAutoFilter(items);

	const duplicateModal = useConditional((item: wf) => {
		return (
			<DuplicateModal
				item={item}
				onDuplicate={(item, newId) => {
					return api.wf_post({
						...item,
						id: newId,
						title: (item.title || "") + " (Copy)",
					}).then(() => {
						bulk.selection.clear();
						setToken(newID());
					});
				}}
				onClose={() => duplicateModal.close()}
			/>
		);
	});

	const bulk = useBulkActions<wf>({
		items: filteredData,
		onDelete: (items) => {
			return Promise.all(items.map(item => api.wf_delete({ id: item.id }))).then(() => {
				setToken(newID());
			});
		},
		onDuplicate: (item) => {
			duplicateModal.show(item);
		},
	});

	const edit = useConditional((wf: wf) => {
		return (
			<EditWorkflow
				wf={wf}
				onSave={() => {
					edit.close();
					setToken(newID());
				}}
				onDelete={() => {
					api.wf_delete({ id: wf.id }).then(() => {
						setToken(newID());
						edit.close();

					}).catch(() => {

					})
				}}
			/>
		)
	})

	return (
		<>
			<AutoFilterList filterID="workflows" data={items} onChange={onFilterChange} />
			<Table
				cols={[
					CheckboxCol({ selection: bulk.selection, items: filteredData }),
					VersionCol(),
					IDCol(),
					CustomCol({
						title: () => "Icon",
						value: (r, c) => {
							return <WorkflowIcon icon={r.icon} />
						},
					}),
					TitleCol(),
					TagsCol(),
					LastUpdatedCol(),
					SyncTableCol({ container: "wf", onSync: () => {
						setToken(newID());
					} }),
					CopyToClipboardCol(),
				]}
				items={filteredData}
				interactive={true}
				onClick={(e, r) => {
					edit.show(r);
				}}
				bulkActions={bulk.bulkActions}
				onBulkAction={bulk.onBulkAction}
			/>
			{edit.render()}
			{duplicateModal.render()}
		</>
	);
}

export function EditWorkflow(props: {
	wf: wf,
	onSave: () => void,
	onDelete: () => void,
}) {
	function save() {
		// can't save the in-memory structure - it has things that shouldn't be persisted
		api.wf_post(props.wf).then(() => {
			props.onSave();

		}).catch(() => {

		});
	}
	
	const [, refresh] = React.useState({});

	const data = useDataContext({ initialValue: props.wf });


	if (!props.wf) return null;

	return(
		<DataContext.Provider value={data}>
			<Modal
				editableTitle={{
					obj: props.wf,
					prop: "title",
				}}
				editableSubtitle={{
					obj: props.wf,
					prop: "id",
				}}
				titleCenter={
					<div className="flex horiz">
						<button
							className="primary"
							onClick={() => {
								props.wf.data.states.push({
									id: newID(),
									title: "New State",
									phase: "",
									transitions: [],
									events: [],
									features: [],
								});
								refresh({});
							}}
						>Create State</button>
						<TagEditor dbObj={props.wf} />

						<Text prop="icon" placeholder="Icon" size={4} />
					</div>
				}
				size="fullscreen"

				onSave={save}
				onCancel={props.onSave}
				showCancel={true}
				cancelOnEscapeKey={true}
				saveOnEnterKey={false}

				showDelete={true}
				onDelete={props.onDelete}
			>

				<Designer wf={props.wf} />

			</Modal>
		</DataContext.Provider>
	);
}
